import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { isAdmin } from '@/utils/adminAuth'
import { CONTEUDO_CHAVES, getConteudoPublic } from '@/api/adminApi'
import {
  canAccessAgendamentoPage,
  resolveAgendamentoFlagsFromData,
} from '@/utils/agendamentoFlags'
import { isConteudoFlagInData } from '@/utils/featureFlags'
import { readConteudoSnapshot, subscribeConteudoUpdated } from '@/utils/conteudoSync'
import type { AgendamentoFlags } from '@/utils/agendamentoFlags'
import {
  getNotificacoesCliente,
  getNotificacoesNaoLidasCount,
  marcarNotificacaoComoLida,
  type NotificacaoResumo,
} from '@/api/usuariosApi'
import { NotificationCard } from '@/components/NotificationCard'
import { useToast } from '@/contexts/ToastContext'
import { getHistoricoAgendamentoPath } from '@/utils/clienteNavigation'
import { getClientPublicEntryPath, isNativeWhiteLabelBuild } from '@/config/tenant'
import { isNativeAppRuntime } from '@/config/runtime'
import { applyNativeSafeAreaInsets } from '@/utils/nativeSafeArea'
import { useTenant } from '@/contexts/TenantContext'
import { isGuestClient } from '@/utils/guestAuth'
import styles from './ClientShell.module.css'

interface ClientShellProps {
  children: ReactNode
}

const TITLE_MAP: Record<string, { title: string; subtitle?: string }> = {
  '/menu': { title: 'Início', subtitle: 'Escolha o que deseja fazer' },
  '/perfil': { title: 'Perfil', subtitle: 'Seus dados e preferências' },
  '/agendamento': { title: 'Agendar', subtitle: 'Escolha unidade, profissional e horário' },
  '/historico': { title: 'Histórico', subtitle: 'Veja os atendimentos anteriores' },
  '/tarefas': { title: 'Tarefas', subtitle: 'Acompanhe o que tem por fazer' },
  '/pacotes': { title: 'Pacotes', subtitle: 'Planos especiais para você' },
  '/servicos': { title: 'Serviços', subtitle: 'Catálogo de tratamentos' },
  '/vip': { title: 'Programa V.I.P', subtitle: 'Benefícios exclusivos' },
  '/quem-somos': { title: 'Quem somos', subtitle: 'A nossa história e valores' },
  '/contatos': { title: 'Contatos', subtitle: 'Fale connosco' },
  '/mapa': { title: 'Mapa', subtitle: 'Como chegar' },
  '/galeria': { title: 'Galeria', subtitle: 'Fotos e vídeos' },
  '/social': { title: 'Comunidade', subtitle: 'Feed e testemunhos' },
  '/notificacoes': { title: 'Notificações', subtitle: 'Lembretes e avisos' },
}

function getTitle(pathname: string): { title: string; subtitle?: string } {
  const base = pathname.split('?')[0]
  const foundKey = Object.keys(TITLE_MAP).find((p) => base === p)
  if (foundKey) return TITLE_MAP[foundKey]
  return { title: 'GoBooking' }
}

export function ClientShell({ children }: ClientShellProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useUser()
  const adminPreview = isAdmin() && !user
  const guestMode = isGuestClient(user)
  const { toast } = useToast()
  const { nome: empresaNome } = useTenant()
  const [notifCount, setNotifCount] = useState(0)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notificacoes, setNotificacoes] = useState<NotificacaoResumo[]>([])
  const [shellFlagsReady, setShellFlagsReady] = useState(false)
  const [pacotesEnabled, setPacotesEnabled] = useState(false)
  const [tarefasEnabled, setTarefasEnabled] = useState(false)
  const [agFlags, setAgFlags] = useState<AgendamentoFlags>({
    fluxo: false,
    historico: false,
    servicosMarcacao: false,
  })
  const [notificacoesEnabled, setNotificacoesEnabled] = useState(false)
  const [vipEnabled, setVipEnabled] = useState(false)

  const { title, subtitle } = getTitle(location.pathname)

  const handleBack = () => {
    if (location.pathname === '/menu') {
      if (adminPreview) {
        navigate('/admin/dashboard', { replace: true })
        return
      }
      window.location.replace(getClientPublicEntryPath())
      return
    }
    navigate(-1)
  }

  const goTo = (path: string) => {
    if (location.pathname === path) return
    navigate(path)
  }

  const isActive = (path: string) => location.pathname === path

  const applyShellFlags = useCallback((data: Record<string, string | null>, ag: AgendamentoFlags) => {
    setPacotesEnabled(isConteudoFlagInData(data, CONTEUDO_CHAVES.pacotes_enabled))
    setTarefasEnabled(isConteudoFlagInData(data, CONTEUDO_CHAVES.tarefas_enabled))
    setAgFlags(ag)
    setNotificacoesEnabled(isConteudoFlagInData(data, CONTEUDO_CHAVES.notificacoes_enabled))
    setVipEnabled(isConteudoFlagInData(data, CONTEUDO_CHAVES.vip_enabled))
    setShellFlagsReady(true)
  }, [])

  const loadShellFlags = useCallback(() => {
    const snap = readConteudoSnapshot()
    const snapHasShell =
      CONTEUDO_CHAVES.pacotes_enabled in snap ||
      CONTEUDO_CHAVES.tarefas_enabled in snap ||
      CONTEUDO_CHAVES.notificacoes_enabled in snap ||
      CONTEUDO_CHAVES.agendamento_fluxo_enabled in snap ||
      CONTEUDO_CHAVES.agendamentos_enabled in snap
    if (snapHasShell) {
      applyShellFlags(snap, resolveAgendamentoFlagsFromData(snap))
    }
    return getConteudoPublic()
      .then((data) => applyShellFlags(data, resolveAgendamentoFlagsFromData(data)))
      .catch(() => {
        /* mantém último estado — não repõe tudo como visível */
      })
  }, [applyShellFlags])

  useEffect(() => {
    let cancelled = false
    const run = () => {
      if (cancelled) return
      void loadShellFlags()
    }
    run()
    const unsub = subscribeConteudoUpdated(run)
    return () => {
      cancelled = true
      unsub()
    }
  }, [loadShellFlags])

  useEffect(() => {
    if (!shellFlagsReady) return
    if (location.pathname === '/agendamento' && !canAccessAgendamentoPage(agFlags)) {
      navigate('/menu', { replace: true })
    }
    if (location.pathname === '/historico' && !agFlags.historico) {
      navigate('/menu', { replace: true })
    }
  }, [shellFlagsReady, agFlags, location.pathname, navigate])

  useEffect(() => {
    if (!shellFlagsReady || notificacoesEnabled) return
    if (location.pathname === '/notificacoes') {
      navigate('/menu', { replace: true })
    }
  }, [shellFlagsReady, notificacoesEnabled, location.pathname, navigate])

  useEffect(() => {
    if (!shellFlagsReady || vipEnabled) return
    if (location.pathname === '/vip') {
      navigate('/menu', { replace: true })
    }
  }, [shellFlagsReady, vipEnabled, location.pathname, navigate])

  useEffect(() => {
    if (!shellFlagsReady || tarefasEnabled) return
    if (location.pathname === '/tarefas') {
      navigate('/menu', { replace: true })
    }
  }, [shellFlagsReady, tarefasEnabled, location.pathname, navigate])

  useEffect(() => {
    const userId = user?.id
    if (typeof userId !== 'number') {
      setNotifCount(0)
      setNotifOpen(false)
      return
    }
    const userIdSafe: number = userId
    let cancelled = false

    async function load() {
      try {
        const [count, list] = await Promise.all([
          getNotificacoesNaoLidasCount(userIdSafe),
          getNotificacoesCliente(userIdSafe, 8),
        ])
        if (!cancelled) {
          setNotifCount(count)
          setNotificacoes(list)
        }
      } catch {
        if (!cancelled) {
          setNotifCount(0)
          setNotificacoes([])
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [user])

  const handleMarcarLida = async (notif: NotificacaoResumo) => {
    if (!user?.id) return
    try {
      await marcarNotificacaoComoLida(notif.id, user.id)
      setNotificacoes((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, lida: 1 } : n))
      )
      setNotifCount((c) => Math.max(0, c - 1))
      if (notif.agendamento_id && agFlags.historico) {
        navigate(getHistoricoAgendamentoPath(notif.agendamento_id))
      }
    } catch {
      toast('Não foi possível atualizar a notificação.', 'error')
    }
  }

  const nativeShell = isNativeWhiteLabelBuild() || isNativeAppRuntime()
  const isMenuHome = location.pathname === '/menu'
  const showBackBtn = !nativeShell || !isMenuHome

  useEffect(() => {
    if (!nativeShell) return
    document.documentElement.classList.add('native-shell-active')
    return () => {
      document.documentElement.classList.remove('native-shell-active')
    }
  }, [nativeShell])

  useEffect(() => {
    if (!nativeShell) return
    return applyNativeSafeAreaInsets()
  }, [nativeShell])

  /* Desbloqueia scroll se um modal anterior deixou overflow preso no body */
  useEffect(() => {
    document.body.style.removeProperty('overflow')
    if (!document.querySelector('[role="dialog"], [role="alertdialog"]')) {
      document.documentElement.classList.remove('app-modal-open')
    }
  }, [location.pathname])

  return (
    <div
      className={`client-shell-root ${styles.root} ${nativeShell ? styles.rootNative : ''}`}
    >
      <header className={`${styles.header} ${nativeShell ? styles.headerNative : ''}`}>
        {adminPreview && (
          <div className={styles.adminPreviewRow} role="status" aria-live="polite">
            <span className={styles.adminPreviewText}>
              <strong>Pré-visualização</strong> — a ver a app do cliente como administrador (sem sessão de
              cliente).
            </span>
            <button
              type="button"
              className={styles.adminPreviewBtn}
              onClick={() => navigate('/admin/dashboard', { replace: true })}
            >
              Voltar ao painel admin
            </button>
          </div>
        )}
        {!adminPreview && guestMode && (
          <div className={styles.guestBanner} role="status" aria-live="polite">
            <span className={styles.guestBannerText}>
              Modo visitante — explore à vontade. Inicie sessão para ver perfil, histórico e
              notificações.
            </span>
          </div>
        )}
        <div className={styles.headerToolbar}>
          <div className={`${styles.headerMain} ${!showBackBtn ? styles.headerMainNoBack : ''}`}>
            {showBackBtn && (
              <button type="button" className={styles.backBtn} onClick={handleBack} aria-label="Voltar">
                ←
              </button>
            )}
            <div className={styles.headerTitles}>
              <div className={styles.titleRow}>
                <div className={styles.title}>{title}</div>
                {nativeShell && (
                  <span className={styles.empresaNomeInline}>{empresaNome}</span>
                )}
              </div>
              {!nativeShell && subtitle && <div className={styles.subtitle}>{subtitle}</div>}
              {!nativeShell && <div className={styles.empresaNome}>{empresaNome}</div>}
            </div>
          </div>
          {!adminPreview && shellFlagsReady && notificacoesEnabled && (
            <div className={styles.headerRight}>
              <button
                type="button"
                className={styles.notifBtn}
                onClick={() => setNotifOpen((open) => !open)}
                aria-label="Ver notificações"
              >
                <span className={styles.notifIcon}>🔔</span>
                {notifCount > 0 && (
                  <span className={styles.notifBadge}>
                    {notifCount > 9 ? '9+' : notifCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className={styles.notifPanel}>
                  <div className={styles.notifPanelHeader}>
                    <span className={styles.notifPanelTitle}>Notificações</span>
                    {notifCount > 0 && (
                      <span className={styles.notifPanelCount}>
                        {notifCount} nova{notifCount > 1 ? 's' : ''}
                      </span>
                    )}
                    <button
                      type="button"
                      className={styles.notifVerTodas}
                      onClick={() => {
                        setNotifOpen(false)
                        navigate('/notificacoes')
                      }}
                    >
                      Ver todas
                    </button>
                  </div>
                  <div className={styles.notifPanelBody}>
                    {notificacoes.length === 0 ? (
                      <div className={styles.notifEmpty}>
                        <span className={styles.notifEmptyIcon} aria-hidden>
                          🔔
                        </span>
                        <p>Sem notificações no momento.</p>
                      </div>
                    ) : (
                      <div className={styles.notifList}>
                        {notificacoes.map((n) => (
                          <NotificationCard
                            key={n.id}
                            notificacao={n}
                            compact
                            onClick={() => handleMarcarLida(n)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <div className={styles.content}>
        <div className={`${styles.contentInner} layout-content`}>{children}</div>
      </div>

      <nav
        className={`${styles.bottomNav} ${nativeShell ? styles.bottomNavNative : ''}`}
        aria-label="Navegação principal"
      >
        <div className={styles.bottomNavInner}>
          <button
            type="button"
            className={`${styles.navItem} ${isActive('/menu') ? styles.navItemActive : ''}`}
            onClick={() => goTo('/menu')}
          >
            <span className={styles.navItemIcon}>🏠</span>
            <span className={styles.navItemLabel}>Início</span>
          </button>
          {shellFlagsReady && agFlags.fluxo && (
            <button
              type="button"
              className={`${styles.navItem} ${isActive('/agendamento') ? styles.navItemActive : ''}`}
              onClick={() => goTo('/agendamento')}
            >
              <span className={styles.navItemIcon}>📅</span>
              <span className={styles.navItemLabel}>Agendar</span>
            </button>
          )}
          {shellFlagsReady && pacotesEnabled && (
            <button
              type="button"
              className={`${styles.navItem} ${isActive('/pacotes') ? styles.navItemActive : ''}`}
              onClick={() => goTo('/pacotes')}
            >
              <span className={styles.navItemIcon}>🎁</span>
              <span className={styles.navItemLabel}>Pacotes</span>
            </button>
          )}
          {shellFlagsReady && agFlags.historico && (
            <button
              type="button"
              className={`${styles.navItem} ${isActive('/historico') ? styles.navItemActive : ''}`}
              onClick={() => goTo('/historico')}
            >
              <span className={styles.navItemIcon}>📜</span>
              <span className={styles.navItemLabel}>Histórico</span>
            </button>
          )}
          {shellFlagsReady && tarefasEnabled && isAdmin() && (
            <button
              type="button"
              className={`${styles.navItem} ${isActive('/tarefas') ? styles.navItemActive : ''}`}
              onClick={() => goTo('/tarefas')}
            >
              <span className={styles.navItemIcon}>✅</span>
              <span className={styles.navItemLabel}>Tarefas</span>
            </button>
          )}
          <button
            type="button"
            className={`${styles.navItem} ${isActive('/perfil') ? styles.navItemActive : ''}`}
            onClick={() => goTo('/perfil')}
          >
            <span className={styles.navItemIcon}>👤</span>
            <span className={styles.navItemLabel}>Perfil</span>
          </button>
        </div>
      </nav>
    </div>
  )
}

